/**
 * Phép tính thuần của màn license cục bộ (ADR-0058 mục 6.3, 8 "Phía khách").
 * Không gọi mạng, không React: chạy được bằng `node --test`.
 */
import { giaiMaJws } from '../../license/nghiep-vu/license.ts';
import type { CanhBaoLicense, DemNguocLicense, LicenseCucBo, LoaiTepNhap, MucDung } from './types.ts';

/** Từ mức này thanh mức dùng chuyển sang "sắp chạm". */
export const NGUONG_SAP_CHAM = 80;

/**
 * Mức dùng từng giới hạn trong license: `max_users`, `max_nodes`, rồi các khoá
 * của `limits` theo thứ tự chữ cái. Giới hạn runtime không báo mức dùng vẫn
 * hiện (để người quản trị biết license cho bao nhiêu), với `dangDung = null`.
 */
export function tinhMucDung(trangThai: LicenseCucBo): MucDung[] {
  const q = trangThai.license?.entitlements;
  if (!q) return [];
  const cap: Array<[string, number]> = [
    ['max_users', q.max_users],
    ['max_nodes', q.max_nodes],
    ...Object.keys(q.limits ?? {})
      .sort()
      .map((k): [string, number] => [k, q.limits[k]]),
  ];
  return cap.map(([khoa, toiDa]) => {
    const dung = trangThai.usage?.[khoa];
    const dangDung = typeof dung === 'number' && Number.isFinite(dung) ? dung : null;
    const phanTram = dangDung === null || toiDa <= 0 ? null : Math.floor((dangDung * 100) / toiDa);
    let muc: MucDung['muc'] = 'khong-ro';
    if (dangDung !== null) {
      if (dangDung >= toiDa) muc = 'da-cham';
      else if (phanTram !== null && phanTram >= NGUONG_SAP_CHAM) muc = 'sap-cham';
      else muc = 'binh-thuong';
    }
    return { khoa, toiDa, dangDung, phanTram, muc };
  });
}

/**
 * Băng cảnh báo theo trạng thái (ADR-0058 mục 6.3): `EXPIRING` vàng, còn lại
 * (trừ `ACTIVE`) đỏ. `GRACE` kèm số ngày còn lại của ân hạn để đếm ngược.
 */
export function canhBaoTheoTrangThai(trangThai: LicenseCucBo | null | undefined): CanhBaoLicense | null {
  if (!trangThai || trangThai.status === 'ACTIVE') return null;
  return {
    trangThai: trangThai.status,
    muc: trangThai.status === 'EXPIRING' ? 'warning' : 'danger',
    soNgay: Math.max(0, trangThai.days_left),
  };
}

/**
 * Đếm ngược cho thẻ license trên thanh điều hướng. `days_left` do License Agent
 * tính tới mốc kế của trạng thái: `not_before` khi `NOT_YET_VALID`, `expires_at`
 * khi `ACTIVE`/`EXPIRING`, `grace_until` khi `GRACE`; `READ_ONLY` và `INVALID`
 * không còn mốc để đếm. Chưa nhập license nào thì là `CHUA_KICH_HOAT`.
 */
export function demNguocLicense(trangThai: LicenseCucBo): DemNguocLicense {
  if (!trangThai.license) return { loai: 'CHUA_KICH_HOAT', soNgay: 0, coDem: false };
  const coDem = trangThai.status !== 'READ_ONLY' && trangThai.status !== 'INVALID';
  return { loai: trangThai.status, soNgay: coDem ? Math.max(0, trangThai.days_left) : 0, coDem };
}

const LOAI_THEO_TYP: Record<string, LoaiTepNhap> = {
  LIC: 'license',
  KEYRING: 'keyring',
  LRL: 'revocation',
};

/**
 * Đoán loại tệp từ `typ` trong header JWS, để người quản trị không phải chọn
 * đúng loại trước khi nhập. Chỉ đọc header để **gợi ý**; kiểm chữ ký là việc
 * của License Agent khi nhận tệp.
 */
export function doanLoaiTep(noiDung: string): LoaiTepNhap | null {
  const jws = giaiMaJws(noiDung);
  const typ = jws?.header.typ;
  return typeof typ === 'string' ? (LOAI_THEO_TYP[typ] ?? null) : null;
}

/** Tệp nhập tối đa 64 KB (DT21 14.4 bước 1); kiểm trước khi gửi để báo ngay cạnh ô chọn tệp. */
export const TEP_TOI_DA_BYTE = 64 * 1024;

/** Trạng thái còn ghi được dữ liệu: các trạng thái khác chỉ đọc hoặc chặn. */
export function ghiDuoc(trangThai: LicenseCucBo['status']): boolean {
  return trangThai === 'ACTIVE' || trangThai === 'EXPIRING' || trangThai === 'GRACE';
}

/** Có nên nhắc gửi `activation-request.json` để gia hạn: sắp hết hạn trở đi, hoặc chưa có license. */
export function canGiaHan(trangThai: LicenseCucBo['status']): boolean {
  return trangThai !== 'ACTIVE' && trangThai !== 'NOT_YET_VALID';
}

export type LoiDanJws = { ma: 'RONG' } | { ma: 'QUA_LON' } | { ma: 'SAI_DANG' };

const JWS_COMPACT = /^[A-Za-z0-9_-]+\.[A-Za-z0-9_-]+\.[A-Za-z0-9_-]+$/;

/**
 * Kiểm hình dạng nội dung license (hoặc keyring, danh sách thu hồi) người quản
 * trị dán vào thay cho tệp: một JWS compact — ba đoạn base64url nối bằng dấu
 * chấm — không quá 64 KB. Bỏ mọi khoảng trắng và xuống dòng trước (email hay
 * ngắt dòng chuỗi dài). Chỉ kiểm hình dạng; chữ ký do License Agent kiểm.
 */
export function kiemDangJws(vanBan: string): { ok: true; jws: string } | { ok: false; loi: LoiDanJws } {
  const jws = vanBan.replace(/\s+/g, '');
  if (jws === '') return { ok: false, loi: { ma: 'RONG' } };
  if (new TextEncoder().encode(jws).length > TEP_TOI_DA_BYTE) return { ok: false, loi: { ma: 'QUA_LON' } };
  if (!JWS_COMPACT.test(jws)) return { ok: false, loi: { ma: 'SAI_DANG' } };
  return { ok: true, jws };
}
